"use client";

import { c as _c } from "react/compiler-runtime";
import { IconLoader2, IconLego, IconMessageCircleFilled } from "@tabler/icons-react";
import { type MotionValue, useAnimationFrame, useMotionTemplate, useMotionValue, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { CHATBOT_NAME } from "@domainstack/constants";
import { Button } from "@domainstack/ui/button";
const MotionButton = m.create(Button);
interface ChatFabProps {
  loading?: boolean;
  onClick: () => void;
}

/**
 * Animated mesh gradient background for the FAB.
 * Extracted to share between mobile and desktop variants.
 */
function MeshGradientBackground(t0) {
  const $ = _c(14);
  const {
    alwaysVisible,
    meshGradient
  } = t0;
  const t1 = alwaysVisible ? 1 : 0;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      opacity: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== alwaysVisible) {
    t3 = alwaysVisible ? {
      opacity: 1
    } : undefined;
    $[2] = alwaysVisible;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== alwaysVisible) {
    t4 = alwaysVisible ? undefined : {
      hover: {
        opacity: 1
      }
    };
    $[4] = alwaysVisible;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      duration: 0.2
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== meshGradient) {
    t6 = {
      background: meshGradient,
      filter: "blur(4px)"
    };
    $[7] = meshGradient;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t6) {
    t7 = <m.span className="pointer-events-none absolute inset-0 rounded-[inherit]" initial={t2} animate={t3} variants={t4} transition={t5} style={t6} />;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export function ChatFab({
  loading = false,
  onClick
}: ChatFabProps) {
  const prefersReducedMotion = useReducedMotion();

  // Animated gradient positions for mesh-like effect
  const x1 = useMotionValue(20);
  const y1 = useMotionValue(30);
  const x2 = useMotionValue(80);
  const y2 = useMotionValue(70);
  const x3 = useMotionValue(60);
  const y3 = useMotionValue(20);
  const x4 = useMotionValue(40);
  const y4 = useMotionValue(80);
  useAnimationFrame(time => {
    if (prefersReducedMotion) return;
    const t = time / 1000;
    // Each spot moves with different frequencies for organic feel
    x1.set(20 + Math.sin(t * 0.7) * 18);
    y1.set(30 + Math.cos(t * 0.9) * 18);
    x2.set(80 + Math.sin(t * 0.8 + 1) * 18);
    y2.set(70 + Math.cos(t * 0.6 + 2) * 18);
    x3.set(60 + Math.sin(t * 0.5 + 3) * 18);
    y3.set(20 + Math.cos(t * 0.7 + 4) * 18);
    x4.set(40 + Math.sin(t * 0.6 + 5) * 18);
    y4.set(80 + Math.cos(t * 0.8 + 1) * 18);
  });
  const meshGradient = useMotionTemplate`
    radial-gradient(ellipse at ${x1}% ${y1}%, rgba(56, 189, 248, 0.8) 0%, transparent 50%),
    radial-gradient(ellipse at ${x2}% ${y2}%, rgba(139, 92, 246, 0.7) 0%, transparent 55%),
    radial-gradient(ellipse at ${x3}% ${y3}%, rgba(236, 72, 153, 0.6) 0%, transparent 45%),
    radial-gradient(ellipse at ${x4}% ${y4}%, rgba(34, 197, 94, 0.5) 0%, transparent 50%)
  `;
  const motionProps = {
    onClick,
    initial: prefersReducedMotion ? false : {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 16
    },
    whileHover: prefersReducedMotion ? undefined : "hover",
    transition: {
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1] as const
    }
  } as const;

  // Render both mobile and desktop variants, use CSS to toggle visibility.
  // This prevents FOIC (Flash of Incorrect Content) since useIsMobile returns
  // false during SSR, causing mobile users to see desktop button first.
  return <>
      {/* Mobile FAB - icon only, always-visible gradient */}
      <MotionButton variant="default" size="icon-lg" aria-label={`Chat with ${CHATBOT_NAME}`} aria-busy={loading} disabled={loading} className="group fixed right-6 bottom-6 z-40 overflow-hidden rounded-full shadow-lg transition-none md:hidden" {...motionProps}>
        <MeshGradientBackground alwaysVisible meshGradient={meshGradient} />
        <span className="relative z-10 flex items-center justify-center">
          {loading ? <IconLoader2 className="size-5 animate-spin text-background/95" /> : <IconMessageCircleFilled className="size-5 text-background/95" />}
        </span>
      </MotionButton>

      {/* Desktop FAB - text + icon, gradient on hover */}
      <MotionButton variant="outline" size="default" aria-label={`Chat with ${CHATBOT_NAME}`} aria-busy={loading} disabled={loading} className="group fixed right-6 bottom-6 z-40 hidden overflow-hidden px-2 shadow-lg backdrop-blur-md transition-none md:flex" {...motionProps}>
        <MeshGradientBackground alwaysVisible={false} meshGradient={meshGradient} />
        <span className="relative z-10 flex items-center gap-1.5 pr-0.5 text-[13px] leading-none font-semibold tracking-tight">
          {loading ? <IconLoader2 className="size-4 animate-spin text-muted-foreground" /> : <IconLego className="size-4 text-muted-foreground group-hover:text-foreground" />}
          Ask {CHATBOT_NAME}
        </span>
      </MotionButton>
    </>;
}